import {CASH_API_HOST} from '../constants/app';
import {VENMO} from '../constants';
import {STRIPE_SESSION_ERROR} from '../constants/stripeConnect';
import getMemberToken from '../utils/getMemberToken';

const fundToFormData = fund =>
  Object.keys(fund).reduce((formData, key) => {
    const value = fund[key];
    if (value !== undefined) {
      if (Array.isArray(value)) {
        if (value.length === 0) {
          formData.append(key, '');
        } else {
          value.forEach(v => {
            formData.append(key, v);
          });
        }
      } else {
        formData.append(key, value);
      }
    }

    return formData;
  }, new FormData());

class CashApi {
  constructor({token, cashApiHost} = {}) {
    this.token = token;
    this.cashApiHost = cashApiHost || CASH_API_HOST;
  }

  getAccountSettingsLink = async ({token = this.token} = {}) => {
    const res = await fetch(`${this.cashApiHost}/v1/stripe-account-links`, {
      method: 'POST',
      headers: {
        'MEMBERSHIP-SESSION-TOKEN': token,
        'content-type': 'application/json',
      },
      body: JSON.stringify({
        type: 'account_update',
        refreshUrl: `${process.env.TK_HOST}/registry/`,
        returnUrl: `${process.env.TK_HOST}/registry/`,
      }),
    });
    if (!res.ok) {
      const error = new Error(res.statusText);
      error.status = res.status;
      throw error;
    }
    const jsonResponse = await res.json();

    return jsonResponse;
  };

  createStripeAccountSession = async ({token = this.token} = {}) => {
    const res = await fetch(`${this.cashApiHost}/v3/stripe-accounts/sessions`, {
      method: 'POST',
      headers: {
        'MEMBERSHIP-SESSION-TOKEN': token,
      },
    });

    if (!res.ok) {
      const error = new Error(res.statusText);
      error.status = res.status;

      if (res.status === 404) {
        error.code = STRIPE_SESSION_ERROR.NOT_FOUND;
      } else if (res.status === 500) {
        error.code = STRIPE_SESSION_ERROR.SERVER_ERROR;
      } else if (res.status === 502) {
        error.code = STRIPE_SESSION_ERROR.GATEWAY_ERROR;
      } else {
        error.code = STRIPE_SESSION_ERROR.UNKNOWN;
      }

      throw error;
    }

    const jsonResponse = await res.json();
    return jsonResponse;
  };

  deleteFund = async ({token = this.token, fundId} = {}) => {
    const res = await fetch(`${this.cashApiHost}/v1/funds/${fundId}`, {
      method: 'DELETE',
      headers: {
        'MEMBERSHIP-SESSION-TOKEN': token,
      },
    });

    if (!res.ok) {
      const error = new Error(res.statusText);
      error.status = res.status;
      throw error;
    }
  };

  createFund = async ({token = this.token, fund} = {}) => {
    const res = await fetch(`${this.cashApiHost}/v1/funds`, {
      method: 'POST',
      headers: {
        'MEMBERSHIP-SESSION-TOKEN': token,
      },
      body: fundToFormData(fund),
    });

    if (!res.ok) {
      const error = new Error(res.statusText);
      error.status = res.status;
      throw error;
    }

    const jsonResponse = await res.json();

    return jsonResponse;
  };

  updateFund = async ({token = this.token, fund} = {}) => {
    const res = await fetch(`${this.cashApiHost}/v1/funds/${fund.id}`, {
      method: 'PATCH',
      headers: {
        'MEMBERSHIP-SESSION-TOKEN': token,
      },
      body: fundToFormData(fund),
    });

    if (!res.ok) {
      const error = new Error(res.statusText);
      error.status = res.status;
      throw error;
    }

    const jsonResponse = await res.json();

    return jsonResponse;
  };

  getUnusedTemplates = async ({token = this.token} = {}) => {
    const res = await fetch(`${this.cashApiHost}/v1/templates/unused`, {
      headers: {
        'MEMBERSHIP-SESSION-TOKEN': token,
      },
    });

    if (!res.ok) {
      const error = new Error(res.statusText);
      error.status = res.status;
      throw error;
    }

    const jsonResponse = await res.json();

    return jsonResponse;
  };

  getTemplates = async ({token = this.token} = {}) => {
    const res = await fetch(`${this.cashApiHost}/v1/templates`, {
      headers: {
        'MEMBERSHIP-SESSION-TOKEN': token,
      },
    });

    if (!res.ok) {
      const error = new Error(res.statusText);
      error.status = res.status;
      throw error;
    }

    const jsonResponse = await res.json();

    return jsonResponse;
  };

  getFunds = async ({token = this.token, includeDeleted = false} = {}) => {
    const res = await fetch(
      `${this.cashApiHost}/v1/funds?includeDeleted=${includeDeleted}`,
      {
        headers: {
          'MEMBERSHIP-SESSION-TOKEN': token,
        },
      },
    );

    if (!res.ok) {
      const error = new Error(res.statusText);
      error.status = res.status;
      throw error;
    }

    const jsonResponse = await res.json();

    return jsonResponse;
  };

  getRegistry = async ({token = this.token} = {}) => {
    const res = await fetch(`${this.cashApiHost}/v1/registry`, {
      method: 'GET',
      headers: {
        'MEMBERSHIP-SESSION-TOKEN': token,
      },
    });

    if (!res.ok && res.status !== 404) {
      const error = new Error(res.statusText);
      error.status = res.status;
      throw error;
    } else if (!res.ok) {
      return null;
    }

    const jsonResponse = await res.json();

    return jsonResponse;
  };

  updateRegistry = async ({token = this.token, isActive} = {}) => {
    const res = await fetch(`${this.cashApiHost}/v1/registry`, {
      method: 'PATCH',
      headers: {
        'content-type': 'application/json',
        'MEMBERSHIP-SESSION-TOKEN': token,
      },
      body: JSON.stringify({isActive}),
    });

    if (!res.ok) {
      const error = new Error(res.statusText);
      error.status = res.status;
      throw error;
    }

    const jsonResponse = await res.json();

    return jsonResponse;
  };

  getBankAccount = async ({token = this.token, refreshAccount = false} = {}) =>
    fetch(
      `${this.cashApiHost}/v2/stripe-accounts?refreshAccount=${refreshAccount}`,
      {
        method: 'GET',
        headers: {
          'MEMBERSHIP-SESSION-TOKEN': token,
        },
      },
    );

  getStripeAccountDetails = async ({token = this.token} = {}) =>
    fetch(`${this.cashApiHost}/v3/stripe-accounts`, {
      method: 'GET',
      headers: {
        'MEMBERSHIP-SESSION-TOKEN': token,
      },
    });

  getAccountBalances = async ({token = this.token} = {}) =>
    fetch(`${this.cashApiHost}/v2/account-balances`, {
      method: 'GET',
      headers: {
        'MEMBERSHIP-SESSION-TOKEN': token,
      },
    });

  getFinancialAccountTransactions = async ({
    token = this.token,
    limit,
    nextPageUrl,
  } = {}) => {
    const searchParams = new URLSearchParams();

    if (nextPageUrl) {
      searchParams.set('next_page_url', nextPageUrl);
    } else if (limit !== undefined) {
      searchParams.set('limit', String(limit));
    }

    const query = searchParams.toString();
    const endpoint = query
      ? `${this.cashApiHost}/v3/financial-accounts/transactions?${query}`
      : `${this.cashApiHost}/v3/financial-accounts/transactions`;

    const res = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'MEMBERSHIP-SESSION-TOKEN': token,
      },
    });

    if (!res.ok) {
      const error = new Error(res.statusText);
      error.status = res.status;
      throw error;
    }

    return res.json();
  };

  getPurchasesFundsAvailability = async ({token = this.token} = {}) => {
    const res = await fetch(
      `${this.cashApiHost}/v3/purchases/funds-availability`,
      {
        method: 'GET',
        headers: {
          'MEMBERSHIP-SESSION-TOKEN': token,
        },
      },
    );

    if (!res.ok) {
      const error = new Error(res.statusText);
      error.status = res.status;
      throw error;
    }

    return res.json();
  };

  getAllFinancialAccountTransactions = async ({token, limit = 100} = {}) => {
    const transactions = [];
    const seenPageUrls = new Set();

    const fetchPage = async pageUrl => {
      const request = {
        ...(token !== undefined ? {token} : {}),
        ...(pageUrl ? {nextPageUrl: pageUrl} : {limit}),
      };
      const response = await this.getFinancialAccountTransactions(request);

      if (!Array.isArray(response?.data)) {
        throw new Error('Invalid financial account transactions response');
      }

      transactions.push(...response.data);

      const nextPageUrl = response?.nextPageUrl;
      if (nextPageUrl === null || nextPageUrl === undefined) {
        return;
      }

      if (typeof nextPageUrl !== 'string' || nextPageUrl.length === 0) {
        throw new Error('Invalid financial account transaction pagination URL');
      }

      if (seenPageUrls.has(nextPageUrl)) {
        throw new Error(
          'Repeated financial account transaction pagination URL',
        );
      }

      seenPageUrls.add(nextPageUrl);
      await fetchPage(nextPageUrl);
    };

    await fetchPage();

    return transactions;
  };

  createBankAccount = ({token = this.token, accountPayload} = {}) =>
    fetch(`${this.cashApiHost}/v2/stripe-accounts`, {
      method: 'POST',
      headers: {
        'MEMBERSHIP-SESSION-TOKEN': token,
        'content-type': 'application/json',
      },
      body: JSON.stringify(accountPayload),
    });

  sendVerificationInfo = ({token = this.token, accountPayload} = {}) =>
    fetch(`${this.cashApiHost}/v2/stripe-accounts`, {
      method: 'PUT',
      headers: {
        'MEMBERSHIP-SESSION-TOKEN': token,
        'content-type': 'application/json',
      },
      body: JSON.stringify(accountPayload),
    });

  getOtpOrUpdateBankAccount = ({token = this.token, payload = {}} = {}) =>
    fetch(`${this.cashApiHost}/v1/bank-account`, {
      method: 'PUT',
      headers: {
        'MEMBERSHIP-SESSION-TOKEN': token,
        'content-type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

  getPaymentAccounts = ({token = this.token} = {}) =>
    fetch(`${this.cashApiHost}/v1/payment-accounts`, {
      method: 'GET',
      headers: {
        'MEMBERSHIP-SESSION-TOKEN': token,
      },
    });

  createVenmoAccount = ({token = this.token, venmoHandle} = {}) =>
    this.createPaymentAccount({
      token,
      paymentMethod: VENMO,
      details: {venmoHandle},
    });

  createPaymentAccount = ({token = this.token, paymentMethod, details} = {}) =>
    fetch(`${this.cashApiHost}/v1/payment-accounts`, {
      method: 'POST',
      headers: {
        'MEMBERSHIP-SESSION-TOKEN': token,
        'content-type': 'application/json',
      },
      body: JSON.stringify({
        paymentMethod,
        details,
      }),
    });

  createKycOnboardingStripeAccount = ({
    token = this.token,
    accountToken,
  } = {}) =>
    fetch(`${this.cashApiHost}/v3/stripe-accounts`, {
      method: 'POST',
      headers: {
        'MEMBERSHIP-SESSION-TOKEN': token,
        'content-type': 'application/json',
      },
      body: JSON.stringify({accountToken}),
    });

  getPayoutMethods = ({token = this.token} = {}) =>
    fetch(`${this.cashApiHost}/v3/payout-methods`, {
      method: 'GET',
      headers: {
        'MEMBERSHIP-SESSION-TOKEN': token,
      },
    });

  createWithdrawal = async ({
    token = this.token,
    bankAccountId,
    amountCents,
  } = {}) =>
    fetch(`${this.cashApiHost}/v3/withdrawals`, {
      method: 'POST',
      headers: {
        'MEMBERSHIP-SESSION-TOKEN': token,
        'content-type': 'application/json',
      },
      body: JSON.stringify({bankAccountId, amountCents}),
    });

  identityVerificationChallenge = ({token = this.token} = {}) =>
    fetch(`${this.cashApiHost}/v1/identity-verification/challenge`, {
      method: 'POST',
      headers: {
        'MEMBERSHIP-SESSION-TOKEN': token,
        'content-type': 'application/json',
      },
      body: JSON.stringify({}),
    });

  identityVerificationVerify = ({token = this.token, otp} = {}) =>
    fetch(`${this.cashApiHost}/v1/identity-verification/verify`, {
      method: 'POST',
      headers: {
        'MEMBERSHIP-SESSION-TOKEN': token,
        'content-type': 'application/json',
      },
      body: JSON.stringify({otp}),
    });
}

export default new CashApi({token: getMemberToken()});
